import { ChangeEvent, FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as XLSX from "xlsx";
import { FileUploader } from "react-drag-drop-files";

import { faUpload } from "@fortawesome/pro-regular-svg-icons";

import {
	useCreateCsvEnrichmentMutation,
	useSuggestCsvMappingMutation,
} from "@/pages/Private/redux/csvEnrichment/csvEnrichment.api";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { InputField } from "@/components/InputField/InputField";

import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";

import { classNames } from "@/utils/classNames";
import { parseCsvTable } from "@/utils/parseCsvText";

import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";

import { MappingFieldRow } from "@/components/ColumnMapping/MappingFieldRow";
import { Icon } from "@/components/Icon/Icon";
import { Spinner } from "@/components/Spinner/Spinner";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { useSingleFlight } from "@/utils/useSingleFlight";

import { List } from "../../List/schema/list";
import { FeaturesCodes } from "../../Users/enum/codes";
import {
	CreateCsvEnrichment,
	CreateCsvEnrichmentSchema,
	CsvEnrichment,
} from "../schema/csvEnrichment";
import { getDefaultOptions, getMinimumFields } from "../utils/mappingFieldConfigs";
import { getMissingMappingCombinations } from "../utils/mappingRequirements";

import { MappingRequirementsHint } from "./MappingRequirementsHint";
import { MappedPreviewModal } from "./titleGate/MappedPreviewModal";
import { TitleScreeningGate } from "./titleGate/TitleScreeningGate";
import { getFirstRowValue, ParsedXlsxData } from "./titleGate/titleGate.utils";
import { useCompanyUploadPreview } from "./titleGate/useCompanyUploadPreview";
import { useTitleScreeningPreview } from "./titleGate/useTitleScreeningPreview";
import { XlsStatistics } from "./titleGate/XlsStatistics";

interface CreateCsvEnrichmentFormProps {
	csvEnrichment?: CsvEnrichment | null;
	list: List;
	handleSelect?: (csvEnrichment: CsvEnrichment | null) => void;
}

export const CreateCsvEnrichmentForm: FunctionComponent<CreateCsvEnrichmentFormProps> = ({
	csvEnrichment,
	list,
	handleSelect,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`csvEnrichment.${key}`);
	const [createCsvEnrichment, { isLoading, error }] = useCreateCsvEnrichmentMutation();
	const [suggestCsvMapping] = useSuggestCsvMappingMutation();
	const { run: runUpload, isRunning: isUploadRunning } = useSingleFlight();
	const [fileContent, setFileContent] = useState<string>();
	const [originalFileName, setOriginalFileName] = useState<string>("");
	const [foundFields, setFoundFields] = useState<{ foundField: string; assignedField: string }[]>();
	const [isAiSuggesting, setIsAiSuggesting] = useState(false);
	const [parsedData, setParsedData] = useState<ParsedXlsxData>();
	const [screenTitles, setScreenTitles] = useState(true);
	const [showMappedPreview, setShowMappedPreview] = useState(false);
	const isScreeningFeatureActive = useIsFeatureActive(FeaturesCodes.JOB_TITLE_SCREENING);

	const initialState = {
		name: "",
		file: "",
		webhook: false,
	};

	const [currentFormState, setCurrentFormState] = useState<CreateCsvEnrichment>(initialState);

	const handleSaveFormState = (
		value: CreateCsvEnrichment[keyof CreateCsvEnrichment],
		name: keyof CreateCsvEnrichment
	) => {
		setCurrentFormState({
			...currentFormState,
			[name]: value,
		});

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const {
		handleSubmit,
		reset,
		setValue,
		formState: { errors, isDirty },
		watch,
	} = useForm<CreateCsvEnrichment>({
		defaultValues: initialState,
		resolver: zodResolver(CreateCsvEnrichmentSchema),
	});

	if (Object.values(errors).length) {
		console.log(errors);
	}

	const formValues = watch();

	useEffect(() => {
		if (csvEnrichment) {
			reset({
				id: csvEnrichment.id,
				name: csvEnrichment.name,
			});
		} else {
			reset(initialState);
		}
	}, [list.scrapeLevel, csvEnrichment, reset]);

	const minimumFields = useMemo(() => getMinimumFields(list.entityType), [list]);

	const defaultOptions = useMemo(() => getDefaultOptions(list.entityType), [list]);

	const { gateVisible, statsVisible, preview, rowStats } = useTitleScreeningPreview({
		entityType: list.entityType,
		foundFields,
		parsedData,
		isFeatureActive: isScreeningFeatureActive,
	});

	const { preview: companyPreview } = useCompanyUploadPreview({
		entityType: list.entityType,
		foundFields,
		parsedData,
	});

	const allowUpload = useMemo(() => {
		if (formValues.webhook) {
			return true;
		}

		if (foundFields) {
			const isMinimumFields = minimumFields.some((field) => {
				return field.every((f) => foundFields.map((m) => m.assignedField).includes(f));
			});

			return isMinimumFields;
		}

		return false;
	}, [foundFields, minimumFields, formValues.webhook]);

	const missingMappingCombinations = useMemo(() => {
		if (formValues.webhook) {
			return null;
		}

		return getMissingMappingCombinations(minimumFields, defaultOptions, foundFields);
	}, [formValues.webhook, minimumFields, defaultOptions, foundFields]);

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const showTitleGate = gateVisible && !formValues.webhook;
	// PREC-3870: the box is not the gate — a company upload and a workspace without the screening
	// feature both get their row statistics.
	const showStats = statsVisible && !formValues.webhook;

	const submitUpload = async (values: CreateCsvEnrichment) => {
		try {
			const { file: fileRaw } = values;

			const formData = new FormData();

			if (fileRaw) {
				const res: Response = await fetch(fileRaw);
				const blob: Blob = await res.blob();
				const file = new File([blob], originalFileName || "file.csv");

				formData.append("file", file);

				formData.append("name", values.name || originalFileName || "");
			} else {
				formData.append("name", values.name || "");
			}

			formData.append("listId", list.id.toString());
			formData.append("type", "csv");
			formData.append("configuration", JSON.stringify(foundFields));
			formData.append("webhook", values.webhook.toString());

			// Only sent while the gate is visible — flag off or no mapped title column means the
			// upload behaves exactly as today (the API re-checks the flag server-side).
			if (showTitleGate) {
				formData.append("screenTitles", screenTitles.toString());
			}

			await createCsvEnrichment(formData).unwrap();
			handleSelect?.(null);
		} catch (err) {
			console.error(err);
		}
	};

	// The file is fetched and turned into a Blob before the mutation starts, so the mutation's own
	// `isLoading` cannot disable the button during that window — a second click uploaded twice
	// (PREC-4554). Guard the whole operation, not just the request.
	const onSubmit = (values: CreateCsvEnrichment) => runUpload(() => submitUpload(values));

	useEffect(() => {
		const processFoundFields = async (fileContent?: string) => {
			if (!fileContent) {
				setFoundFields(undefined);
				setParsedData(undefined);

				return;
			}

			const res = await fetch(fileContent);

			const buffer = await res.arrayBuffer();

			const workbook = XLSX.read(buffer);
			const content = XLSX.utils.sheet_to_csv(workbook.Sheets[workbook.SheetNames[0]]);

			// `sheet_to_csv` quotes any cell holding a comma, so the text has to be parsed rather
			// than split — see parseCsvText (PREC-3867).
			const { headers: fields, rows: dataRows } = parseCsvTable(content);
			const fieldsToConsider = fields.filter((field) => !!field);

			setParsedData({ headers: fields, rows: dataRows });

			// First 3 data rows for the AI suggestion
			const sampleRows = dataRows.slice(0, 3);

			// Immediate fallback: use compares-based matching
			const fallbackFields: { foundField: string; assignedField: string }[] = fieldsToConsider.map(
				(field) => {
					const option = defaultOptions.find((o) => o.compares?.includes(field));

					return {
						foundField: field,
						assignedField: option?.id || "",
					};
				}
			);

			setFoundFields(fallbackFields);

			// Call AI suggestion endpoint in the background
			if (sampleRows.length > 0) {
				setIsAiSuggesting(true);

				try {
					const result = await suggestCsvMapping({
						entityType: list.entityType as CsvEnrichmentType,
						headers: fieldsToConsider,
						sampleRows,
					}).unwrap();

					if (result.mapping?.length) {
						// Only apply AI suggestions for valid assignedField values
						const validFieldIds = defaultOptions.map((o) => o.id);

						const aiFields = fieldsToConsider.map((field) => {
							const aiMatch = result.mapping.find((m) => m.foundField === field);
							const assignedField = aiMatch?.assignedField || "";

							return {
								foundField: field,
								assignedField: validFieldIds.includes(assignedField) ? assignedField : "",
							};
						});

						setFoundFields(aiFields);
					}
				} catch {
					// AI suggestion failed — keep fallback mapping
				} finally {
					setIsAiSuggesting(false);
				}
			}
		};

		processFoundFields(fileContent);
	}, [fileContent]);

	const options = useMemo(() => {
		return defaultOptions.filter((a) => {
			if (a.id === "customField" || a.id === "") {
				return true;
			}

			if (foundFields?.map((m) => m.assignedField).includes(a.id)) {
				return false;
			}

			return true;
		});
	}, [foundFields]);

	return (
		<div className="p-4 mt-8 rounded-xl border border-gray-200 bg-brand-white">
			<div className="text-md mb-5">Add a source</div>
			<div className="flex flex-row gap-4 w-full mb-4">
				<div className="w-auto">
					<Button
						color={!formValues.webhook ? ButtonColor.ACTION : ButtonColor.ACTION_SECONDARY}
						size={ButtonSize.ML}
						title="Upload an XLSX file"
						onClick={() => setValue("webhook", false)}
					/>
				</div>
				<div className="w-auto">
					<Button
						color={formValues.webhook ? ButtonColor.ACTION : ButtonColor.ACTION_SECONDARY}
						size={ButtonSize.ML}
						title="Fill via webhook"
						onClick={() => setValue("webhook", true)}
					/>
				</div>
			</div>

			{(!!fileContent || formValues.webhook) && (
				<div className="flex flex-row w-full mb-1">
					<InputField
						error={!!errors.name?.message}
						handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
							handleSaveFormState(event.target.value, "name");
						}}
						label={formValues.webhook ? "Source name" : ts("name")}
						name={"name"}
						placeholder={formValues.webhook ? "Source name" : ts("namePlaceholder")}
						value={currentFormState?.name || ""}
					/>
				</div>
			)}

			{showTitleGate && (
				<TitleScreeningGate
					isChecked={screenTitles}
					preview={preview}
					onChange={() => setScreenTitles(!screenTitles)}
				/>
			)}

			{showStats && (
				<XlsStatistics
					companyPreview={companyPreview}
					entityType={list.entityType}
					preview={preview}
					rowStats={rowStats}
				/>
			)}

			{fileContent || formValues.webhook ? (
				<></>
			) : (
				<div className="flex flex-row gap-4 w-full mb-4">
					<FileUploader
						handleChange={(file: File | File[]) => {
							const singleFile = Array.isArray(file) ? file[0] : file;

							setOriginalFileName(singleFile.name);

							if (!currentFormState.name) {
								handleSaveFormState(singleFile.name, "name");
							}

							setValue("file", URL.createObjectURL(singleFile));
							setFileContent(URL.createObjectURL(singleFile));
						}}
						name="file"
						types={["xlsx"]}
					>
						<div className="flex flex-col items-center justify-center w-full bg-white cursor-pointer border-2 border-dotted border-border-light gap-4 p-[80px] rounded-xl whitespace-nowrap">
							<Icon className="text-text-meta w-8 h-8" icon={faUpload} />
							<span>
								Drag and Drop file here or{" "}
								<span className="text-text-meta underline">Choose File</span>
							</span>
						</div>
					</FileUploader>
				</div>
			)}
			{!formValues.webhook && (
				<div className="flex flex-col w-full mb-4">
					{foundFields ? (
						<div className="flex items-center gap-2 mt-4 mb-2">
							<div className="text-text text-left text-ssm font-semibold">{ts("foundFields")}</div>
							{isAiSuggesting && (
								<div className="flex items-center gap-1.5 text-text-meta text-xs">
									<Spinner color="#4d66f5" />
									<span>{t("csvEnrichment.aiAnalyzing")}</span>
								</div>
							)}
						</div>
					) : (
						""
					)}
					<div className="w-full">
						<ul>
							{foundFields
								?.filter((a) => a.foundField)
								?.map((item, index) => (
									<MappingFieldRow
										key={`list-index-${index}`}
										foundField={item.foundField}
										handleSelect={(value?: AutoCompleteItem) => {
											if (value) {
												setFoundFields([
													...foundFields.slice(0, index),
													{
														foundField: item.foundField.trim().replaceAll(".", " "),
														assignedField: value?.id?.toString()?.trim() || "",
													},
													...foundFields.slice(index + 1),
												]);
											}
										}}
										isMapped={!!item.assignedField && item.assignedField !== ""}
										options={options}
										previewValue={getFirstRowValue(parsedData, item.foundField)}
										selectedField={defaultOptions.find((o) => o.id === item.assignedField)}
									/>
								))}
						</ul>
					</div>
				</div>
			)}

			<div className="mb-2">
				{getAllErrors(error, formErrors).length
					? renderErrorMessages(getAllErrors(error, formErrors))
					: null}
			</div>
			<div className="flex gap-4 w-full justify-between items-start">
				<div className="flex-1">
					{missingMappingCombinations && (
						<MappingRequirementsHint combinations={missingMappingCombinations} />
					)}
				</div>
				<div className="flex gap-4">
					{!!fileContent && !formValues.webhook && (
						<div className="flex w-auto">
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								title={t("csvEnrichment.titleGate.previewButton")}
								onClick={() => setShowMappedPreview(true)}
							/>
						</div>
					)}
					<div className="flex w-auto">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							title="cancel"
							onClick={() => {
								handleSelect?.(null);
								setFileContent(undefined);
								setFoundFields(undefined);
								setParsedData(undefined);
								setScreenTitles(true);
								setOriginalFileName("");
								setCurrentFormState(initialState);
								reset(initialState);
							}}
						/>
					</div>
					<div className="flex w-auto">
						<Button
							disabled={!allowUpload}
							isLoading={isLoading || isUploadRunning}
							title={csvEnrichment?.id ? "Save" : "Upload"}
							onClick={handleSubmit(onSubmit)}
						/>
					</div>
				</div>
			</div>
			<MappedPreviewModal
				foundFields={foundFields}
				handleClose={() => setShowMappedPreview(false)}
				isOpened={showMappedPreview}
				options={defaultOptions}
				parsedData={parsedData}
			/>
		</div>
	);
};
